import React from 'react'
import Styles from './FacebookPhotoBox.module.scss'
import cx from 'classnames'
import { Skeleton } from 'antd'
import { MediaObject } from 'types'
import { MediaRenderer } from '../components/MediaRenderer/MediaRenderer'
import { DesignChannel } from 'components/NewFileModal/Types'

interface FacebookPhotoBoxProps {
  mediaObjects?: MediaObject[]
  imagesLoading?: boolean
  className?: string
  showVideoControls?: boolean
  videoFit?: 'contain' | 'cover'
  isActive?: boolean
  isInsertingMedia?: boolean
  isPressable?: boolean
  isGenerating?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  onClose?: () => void
  content?: string
}

export const FacebookPhotoBox: React.FC<FacebookPhotoBoxProps> = ({
  mediaObjects,
  imagesLoading,
  className,
  showVideoControls,
  videoFit,
  isActive = true,
  isInsertingMedia = false,
  isPressable = true,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  content,
}) => {
  const renderImages = () => {
    if (!mediaObjects?.length) return null

    const images = mediaObjects.map((f) => f.thumbnail_url || f.source_url)

    switch (mediaObjects.length) {
      case 1:
        return (
          <div className={Styles.imageContainer}>
            <MediaRenderer
              channel={DesignChannel.FacebookPost}
              mediaObject={mediaObjects[0]}
              alt="facebook-1"
              className={cx(Styles.image, Styles.single)}
              showVideoControls={showVideoControls}
              videoFit={videoFit}
              isActive={isActive}
              isPressable={isPressable}
              showHoverActionOverlay={showHoverActionOverlay}
              almanacFileKey={almanacFileKey}
              isGenerating={isGenerating}
              onClose={onClose}
              allMediaObjects={mediaObjects}
              caption={content}
            />
          </div>
        )
      case 2:
        return (
          <div className={Styles.row}>
            {mediaObjects.map((mediaObject, index) => (
              <div key={index} className={cx(Styles.imageContainer, Styles.half)}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObject}
                  alt={`facebook-${index + 1}`}
                  className={Styles.image}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  isGenerating={isGenerating}
                  onClose={onClose}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
            ))}
          </div>
        )
      case 3:
        return (
          <div className={Styles.row}>
            <div className={cx(Styles.column, Styles.twoThirds)}>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[0]}
                  alt="facebook-1"
                  className={cx(Styles.image, Styles.square)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  isGenerating={isGenerating}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  onClose={onClose}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
            </div>
            <div className={cx(Styles.column, Styles.oneThird)}>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[1]}
                  alt="facebook-2"
                  className={cx(Styles.image, Styles.square)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  isGenerating={isGenerating}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  onClose={onClose}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[2]}
                  alt="facebook-3"
                  className={cx(Styles.image, Styles.square)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  isGenerating={isGenerating}
                  onClose={onClose}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
            </div>
          </div>
        )
      case 4:
        return (
          <div className={Styles.row}>
            <div className={cx(Styles.column, Styles.twoThirds)}>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[0]}
                  alt="facebook-1"
                  className={cx(Styles.image)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  isGenerating={isGenerating}
                  onClose={onClose}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
            </div>
            <div className={cx(Styles.column, Styles.oneThird)}>
              {mediaObjects.slice(1).map((mediaObject, index) => (
                <div key={index} className={Styles.imageContainer}>
                  <MediaRenderer
                    channel={DesignChannel.FacebookPost}
                    mediaObject={mediaObject}
                    alt={`facebook-${index + 2}`}
                    className={cx(Styles.image, Styles.square)}
                    showVideoControls={showVideoControls}
                    videoFit={videoFit}
                    isActive={isActive}
                    showHoverActionOverlay={showHoverActionOverlay}
                    almanacFileKey={almanacFileKey}
                    isGenerating={isGenerating}
                    onClose={onClose}
                    allMediaObjects={mediaObjects}
                    caption={content}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      case 5:
        return (
          <div className={Styles.container}>
            <div className={cx(Styles.row, Styles.twoThirds)}>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[0]}
                  alt="facebook-1"
                  className={cx(Styles.image, Styles.square, Styles.halfFixed)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  isGenerating={isGenerating}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[1]}
                  alt="facebook-2"
                  className={cx(Styles.image, Styles.square, Styles.halfFixed)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  isGenerating={isGenerating}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
            </div>
            <div className={cx(Styles.row, Styles.oneThirdFixed)}>
              {mediaObjects.slice(2).map((mediaObject, index) => (
                <div key={index} className={Styles.imageContainer}>
                  <MediaRenderer
                    channel={DesignChannel.FacebookPost}
                    mediaObject={mediaObject}
                    alt={`facebook-${index + 3}`}
                    className={cx(Styles.image, Styles.square, Styles.fixedSquare)}
                    showVideoControls={showVideoControls}
                    videoFit={videoFit}
                    isActive={isActive}
                    showHoverActionOverlay={showHoverActionOverlay}
                    almanacFileKey={almanacFileKey}
                    isGenerating={isGenerating}
                    allMediaObjects={mediaObjects}
                    caption={content}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      default:
        return (
          <div className={Styles.container}>
            <div className={cx(Styles.row, Styles.twoThirds)}>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[0]}
                  alt="facebook-1"
                  className={cx(Styles.image, Styles.square, Styles.halfFixed)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  isGenerating={isGenerating}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
              <div className={Styles.imageContainer}>
                <MediaRenderer
                  channel={DesignChannel.FacebookPost}
                  mediaObject={mediaObjects[1]}
                  alt="facebook-2"
                  className={cx(Styles.image, Styles.square, Styles.halfFixed)}
                  showVideoControls={showVideoControls}
                  videoFit={videoFit}
                  isActive={isActive}
                  showHoverActionOverlay={showHoverActionOverlay}
                  almanacFileKey={almanacFileKey}
                  isGenerating={isGenerating}
                  allMediaObjects={mediaObjects}
                  caption={content}
                />
              </div>
            </div>
            <div className={cx(Styles.row, Styles.oneThirdFixed)}>
              {mediaObjects.slice(2, 5).map((mediaObject, index) => (
                <div key={index} className={Styles.imageContainer}>
                  <MediaRenderer
                    channel={DesignChannel.FacebookPost}
                    mediaObject={mediaObject}
                    alt={`facebook-${index + 2}`}
                    className={cx(Styles.image, Styles.square, Styles.fixedSquare)}
                    showVideoControls={showVideoControls}
                    videoFit={videoFit}
                    isActive={isActive}
                    showHoverActionOverlay={showHoverActionOverlay}
                    almanacFileKey={almanacFileKey}
                    isGenerating={isGenerating}
                    allMediaObjects={mediaObjects}
                    caption={content}
                  />
                  {index === 2 && (
                    <div className={cx(Styles.image, Styles.square, Styles.fixedSquare, Styles.overlay)}>
                      <span>+{images.length - 5}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
    }
  }

  const renderLoaders = (loaders?: number[]) => {
    const placeholders = loaders || mediaObjects?.map((mo) => mo.id)
    if (!placeholders) return null

    switch (placeholders.length) {
      case 1:
        return <Skeleton active paragraph={{ rows: 1 }} title={false} className={cx(Styles.image, Styles.single)} />
      case 2:
        return (
          <div className={Styles.row}>
            {placeholders.map((index) => (
              <Skeleton
                key={index}
                active
                paragraph={{ rows: 1 }}
                title={false}
                className={cx(Styles.image, Styles.half)}
              />
            ))}
          </div>
        )
      case 3:
        return (
          <div className={Styles.row}>
            <div className={cx(Styles.column, Styles.twoThirds)}>
              <Skeleton active paragraph={{ rows: 1 }} title={false} className={cx(Styles.image, Styles.square)} />
            </div>
            <div className={cx(Styles.column, Styles.oneThird)}>
              <Skeleton active paragraph={{ rows: 1 }} title={false} className={cx(Styles.image, Styles.square)} />
              <Skeleton active paragraph={{ rows: 1 }} title={false} className={cx(Styles.image, Styles.square)} />
            </div>
          </div>
        )
      case 4:
        return (
          <div className={Styles.row}>
            <div className={cx(Styles.column, Styles.twoThirds)}>
              <Skeleton active paragraph={{ rows: 1 }} title={false} className={cx(Styles.image)} />
            </div>
            <div className={cx(Styles.column, Styles.oneThird)}>
              {placeholders.slice(1).map((index) => (
                <Skeleton
                  key={index}
                  active
                  paragraph={{ rows: 1 }}
                  title={false}
                  className={cx(Styles.image, Styles.square)}
                />
              ))}
            </div>
          </div>
        )
      case 5:
      default:
        return (
          <div className={Styles.container}>
            <div className={cx(Styles.row, Styles.twoThirds)}>
              <Skeleton
                active
                paragraph={{ rows: 1 }}
                title={false}
                className={cx(Styles.image, Styles.square, Styles.halfFixed)}
              />
              <Skeleton
                active
                paragraph={{ rows: 1 }}
                title={false}
                className={cx(Styles.image, Styles.square, Styles.halfFixed)}
              />
            </div>
            <div className={cx(Styles.row, Styles.oneThirdFixed)}>
              {placeholders.slice(2).map((index) => (
                <Skeleton
                  key={index}
                  active
                  paragraph={{ rows: 1 }}
                  title={false}
                  className={cx(Styles.image, Styles.square, Styles.fixedSquare)}
                />
              ))}
            </div>
          </div>
        )
    }
  }

  if (imagesLoading || isInsertingMedia)
    return <div className={cx(Styles.photoBox, className)}>{renderLoaders([1])}</div>

  return <div className={cx(Styles.photoBox, className)}>{renderImages()}</div>
}

export default FacebookPhotoBox
